import { useState } from "react";
import { Lock, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface AdminAuthData {
  name: string;
  token: string;
  isSuperAdmin: boolean;
  role: 'admin' | 'warehouse';
  locationId: string | null;
  locationCode: string | null;
  locationName: string | null;
}

interface AdminLoginProps {
  onSuccess: (authData: AdminAuthData) => void;
}

const AdminLogin = ({ onSuccess }: AdminLoginProps) => {
  const { toast } = useToast();
  const [pin, setPin] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [lockoutTime, setLockoutTime] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (pin.length < 4) {
      toast({
        title: "PIN inválido",
        description: "El PIN debe tener al menos 4 dígitos",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      // Use server-side PIN verification with rate limiting
      const { data, error } = await supabase.functions.invoke("verify-admin-pin", {
        body: { action: "login", pin },
      });

      if (error) throw error;

      if (data.rateLimited) {
        setLockoutTime(data.retryAfter);
        toast({
          title: "Demasiados intentos",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      if (data.success && data.token) {
        toast({
          title: "¡Bienvenido!",
          description: data.locationName 
            ? `Hola, ${data.name} - ${data.locationName}`
            : `Hola, ${data.name} (Super Admin)`,
        });
        setRemainingAttempts(null);
        onSuccess({
          name: data.name,
          token: data.token,
          isSuperAdmin: data.isSuperAdmin || false,
          role: data.role || 'admin',
          locationId: data.locationId || null,
          locationCode: data.locationCode || null,
          locationName: data.locationName || null,
        });
      } else {
        setRemainingAttempts(data.remainingAttempts ?? null);
        toast({
          title: "PIN incorrecto",
          description: data.remainingAttempts !== undefined 
            ? `Intentos restantes: ${data.remainingAttempts}`
            : "El código no es válido",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Error",
        description: "No se pudo verificar el PIN",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinInput = (value: string) => {
    // Only allow numbers
    const numericValue = value.replace(/\D/g, "").slice(0, 6);
    setPin(numericValue);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border-border">
        <CardHeader className="text-center pb-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mx-auto mb-4">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="font-serif text-2xl text-primary">
            Panel de Administración
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Introduce el PIN de acceso
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="password"
              inputMode="numeric"
              placeholder="• • • •"
              value={pin}
              onChange={(e) => handlePinInput(e.target.value)}
              className="text-center text-3xl tracking-[0.5em] h-16"
              autoFocus
              disabled={lockoutTime !== null && lockoutTime > 0}
            />
            
            {remainingAttempts !== null && remainingAttempts < 5 && remainingAttempts > 0 && (
              <div className="flex items-center gap-2 text-amber-600 text-sm justify-center">
                <AlertTriangle className="h-4 w-4" />
                <span>Intentos restantes: {remainingAttempts}</span>
              </div>
            )}
            
            {lockoutTime !== null && lockoutTime > 0 && (
              <div className="flex items-center gap-2 text-destructive text-sm justify-center">
                <AlertTriangle className="h-4 w-4" />
                <span>Cuenta bloqueada. Espera {Math.ceil(lockoutTime / 60)} minutos.</span>
              </div>
            )}
            
            <Button
              type="submit"
              size="lg"
              className="w-full h-14 text-lg bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              disabled={isLoading || pin.length < 4 || (lockoutTime !== null && lockoutTime > 0)}
            >
              {isLoading ? (
                "Verificando..."
              ) : (
                <>
                  Acceder
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
